import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { hasEnPosts } from '@/generated/blog/flags';

const Footer = () => {
  const serviceLinks = [
    { label: 'Auto Insurance', href: '/auto-insurance' },
    { label: 'Home Insurance', href: '/home-insurance' },
    { label: 'Business Insurance', href: '/business-insurance' },
    { label: 'Travel Insurance', href: '/travel-insurance' },
    { label: 'Life Insurance', href: '/life-insurance' },
    { label: 'Group Benefits', href: '/group-benefits' },
    { label: 'Marine Insurance', href: '/marine-insurance' }
  ];

  const companyLinks = [
    { label: 'About Us', href: '/about-us' },
    // 'Meet the Team' hidden until the team page has real content (noindex).
    // 'Blog' hidden until at least one English post exists.
    ...(hasEnPosts ? [{ label: 'Blog', href: '/blog' }] : [])
  ];

  const supportLinks = [
    { label: 'Contact Us', href: '/contact-us' },
    { label: 'Claims Support', href: '/claims-support' }
  ];

  return (
    <>
      {/* Divider above footer */}
      <div className="border-t border-neutral-200"></div>
      
      <footer className="bg-neutral-50" data-testid="footer">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1180px] py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <img 
              src="https://customer-assets.emergentagent.com/job_jfi-redesign-1/artifacts/jgq5um9c_JFI-logo-large-webonly%2003jan2026.png" 
              alt="John Fleming Insurance - Burnaby BC"
              className="h-10 w-auto mb-4"
              style={{ maxWidth: 'none' }}
            />
            <p className="text-sm text-neutral-600 mb-4" style={{fontSize: '14px', fontWeight: '400'}}>Local insurance, done right since 1985.</p>
            <div className="space-y-2 text-sm text-neutral-600" style={{fontSize: '13px', fontWeight: '400'}}>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  6696 Hastings St<br />
                  Burnaby, BC V5B 1S3
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                <a href="tel:604-298-1218" className="hover:text-cta">604-298-1218</a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <a href="mailto:info@jfi.bc.ca" className="hover:text-cta">info@jfi.bc.ca</a>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="h-4 w-4 mt-0.5" />
                <div>
                  Mon-Fri: 9am-6pm<br />
                  Sat: 9am-5pm
                </div>
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-bold text-neutral-900 mb-4" style={{fontSize: '16px', fontWeight: '600'}}>Services</h3>
            <ul className="space-y-2 leading-6">
              {serviceLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-neutral-600 hover:text-cta hover:underline transition-colors duration-150" style={{fontSize: '14px', fontWeight: '400'}}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="font-bold text-neutral-900 mb-4" style={{fontSize: '16px', fontWeight: '600'}}>Company</h3>
            <ul className="space-y-2 leading-6">
              {companyLinks.map((link) => (
                <li key={link.href}>
                  <Link to={link.href} className="text-sm text-neutral-600 hover:text-cta hover:underline transition-colors duration-150" style={{fontSize: '14px', fontWeight: '400'}}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h3 className="font-bold text-neutral-900 mb-4" style={{fontSize: '16px', fontWeight: '600'}}>Support</h3>
            <ul className="space-y-2 leading-6">
              {supportLinks.map((link) => (
                <li key={link.href}>
                  {link.href.startsWith('http') ? (
                    <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-sm text-neutral-600 hover:text-cta hover:underline transition-colors duration-150" style={{fontSize: '14px', fontWeight: '400'}}>
                      {link.label}
                    </a>
                  ) : (
                    <Link to={link.href} className="text-sm text-neutral-600 hover:text-cta hover:underline transition-colors duration-150" style={{fontSize: '14px', fontWeight: '400'}}>
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="my-8" />

        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-neutral-600" style={{fontSize: '13px', fontWeight: '400'}}>
          <div>
            © {new Date().getFullYear()} John Fleming Insurance. All rights reserved.
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center px-2 py-1 rounded-md bg-cta/10 text-cta text-xs font-medium" style={{fontSize: '12px', fontWeight: '600'}}>
              Burnaby, BC
            </span>
          </div>
        </div>

        {/* Partners Mini-List */}
        <div className="mt-6 pt-6 border-t border-neutral-200">
          <p className="text-xs text-neutral-500 text-center">
            <span className="font-medium text-neutral-600">Partners:</span> Intact · Aviva · Gore Mutual · ICBC · Wawanesa · Optimum · Cansure · Allianz · Chubb · <Link to="/partners" className="text-cta hover:text-cta-hover underline">More...</Link>
          </p>
        </div>

        {/* Legal Links */}
        <div className="mt-4 pt-4 border-t border-neutral-200">
          <div className="flex flex-wrap justify-center gap-4 text-xs text-neutral-500">
            <Link to="/privacy-policy" className="hover:text-cta transition-colors">
              Privacy Policy
            </Link>
            <span className="text-neutral-300">|</span>
            <Link to="/terms-of-use" className="hover:text-cta transition-colors">
              Terms of Use
            </Link>
            <span className="text-neutral-300">|</span>
            <button 
              onClick={() => window.openCookieSettings && window.openCookieSettings()} 
              className="hover:text-cta transition-colors cursor-pointer"
            >
              Cookie Preferences
            </button>
          </div>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
